import type {FlowNode, FlowNodeStatus, NodeBehavior} from '../../../ui/StageFlow/StageFlowView';
import type Milestone from '../../../entities/milestone';

/**
 * Lead milestone policy (OWA-689).
 *
 * The pipeline completes strictly in order, so a milestone's state is entirely
 * derivable from which rows carry a `dateCompleted` — there is no stored status
 * field and none is needed. Everything here is pure and free of store imports so
 * the rules can be asserted directly.
 */

/** Positions 1 to 6, in order. Mirrors MilestoneHelper.N_* constants. */
export const PIPELINE_POSITIONS = [1, 2, 3, 4, 5, 6];

/** The position whose completion means the retainer is funded, i.e. Won. */
export const WIN_POSITION = 6;

export type ResolutionType = 'won-retainer' | 'won-consult' | 'refused' | 'lost' | 'waiting';

export type ResolutionDef = {
    type: ResolutionType;
    position: number;
    /** Which `milestone:reasons:*` lookup path the reasons come from. */
    reasonPath: 'won' | 'refused' | 'lost' | 'waiting';
    /** Ends the pipeline. A parked lead keeps its pipeline live. */
    terminal: boolean;
};

export const RESOLUTIONS: ResolutionDef[] = [
    {type: 'won-retainer', position: 6, reasonPath: 'won', terminal: true},
    {type: 'won-consult', position: 6.1, reasonPath: 'won', terminal: true},
    {type: 'refused', position: 7, reasonPath: 'refused', terminal: true},
    {type: 'lost', position: 8, reasonPath: 'lost', terminal: true},
    {type: 'waiting', position: 9, reasonPath: 'waiting', terminal: false},
];

/** Resolutions the user picks from the Resolution row (Won-by-retainer is earned in the pipeline). */
export const SELECTABLE_RESOLUTIONS = RESOLUTIONS.filter(r => r.type !== 'won-retainer');

export const isWonResolution = (type: ResolutionType) =>
    type === 'won-retainer' || type === 'won-consult';

export type MilestoneFlowNode = FlowNode & {
    position: number;
    milestone: Milestone | null;
    /**
     * The next milestone to record. This is not a state the lead is "in" — it
     * only marks which placeholder is actionable, since the pipeline completes
     * in order. It looks identical to the other placeholders.
     */
    isNext: boolean;
};

const completedAt = (ms: Milestone | undefined | null): Date | null =>
    ms?.dateCompleted ? new Date(ms.dateCompleted) : null;

const byPosition = (milestones: Milestone[], position: number): Milestone | null =>
    milestones.find(ms => ms.position === position && ms.status !== 'D') ?? null;

/**
 * The lead's current resolution, or null while it is still in the pipeline.
 * Won-by-retainer wins over everything; a parked lead yields to any terminal one.
 */
export const getResolution = (milestones: Milestone[]): {def: ResolutionDef; milestone: Milestone} | null => {
    for (const def of RESOLUTIONS) {
        const ms = byPosition(milestones, def.position);
        if (ms?.dateCompleted) return {def, milestone: ms};
    }
    return null;
};

/** True when the lead has been resolved in a way that closes the pipeline. */
export const isTerminallyResolved = (milestones: Milestone[]): boolean =>
    !!getResolution(milestones)?.def.terminal;

/**
 * Build the pipeline nodes. Status is derived, never stored, and there are only
 * two states a milestone can be in: it happened, or it has not happened yet.
 * There is deliberately no "active" state — a lead is never *in* a milestone,
 * because every milestone names something already completed. Once the lead is
 * resolved early, the milestones it never got to become unreachable.
 */
export const deriveMilestoneNodes = (
    milestones: Milestone[],
    nameFor: (position: number) => string
): MilestoneFlowNode[] => {
    const resolution = getResolution(milestones);
    // A win by retainer is the pipeline finishing, not an early exit.
    const closedEarly = !!resolution?.def.terminal && resolution.def.type !== 'won-retainer';

    let nextAssigned = false;

    return PIPELINE_POSITIONS.map(position => {
        const ms = byPosition(milestones, position);
        const completed = completedAt(ms);

        let stageStatus: FlowNodeStatus;
        let isNext = false;

        if (completed) stageStatus = 'c';
        else if (closedEarly) stageStatus = 's';
        else {
            stageStatus = 'p';
            if (!nextAssigned) {
                isNext = true;
                nextAssigned = true;
            }
        }

        return {
            id: ms?.id ?? `milestone-position-${position}`,
            name: nameFor(position),
            sortOrder: position,
            color: null,
            stageStatus,
            enteredOn: null,
            completedOn: completed,
            position,
            milestone: ms,
            isNext,
        };
    });
};

export type MilestoneNodeHandlers = {
    /** Record the milestone the lead has now reached. */
    onComplete: (node: MilestoneFlowNode) => void;
    /** Return to an earlier milestone, undoing it and everything after. */
    onMoveBack: (node: MilestoneFlowNode) => void;
    /** The final pipeline step opens the Won panel rather than completing silently. */
    onWinPrompt: (node: MilestoneFlowNode) => void;
    /** Reopen the lead. Offered on whichever node holds the resolution. */
    onReopen?: () => void;
    /** Correct the reasons and notes recorded with the resolution, without clearing it. */
    onEditResolution?: () => void;
};

/**
 * What a milestone lets you do. The pipeline records one milestone at a time in
 * order: only the next unrecorded milestone can be clicked, and only an
 * already-recorded one can be undone. There is no jumping ahead, so Won is
 * reachable only by recording Retainer Signed and then Retainer Funded. A
 * resolved lead is frozen until it is reopened.
 */
/**
 * A completed milestone whose date disagrees with the record around it, or with
 * the calendar. Advisory only, and deliberately so: correcting a date keyed
 * wrong last week is the ordinary case, and the firm's real sequence is their
 * business, not ours to enforce. Worth saying out loud because analytics read
 * `dateCompleted` for monthly outcomes — a future or out-of-order date quietly
 * reports a lead retained before it was ever contacted.
 */
export const milestoneDateWarning = (
    node: MilestoneFlowNode,
    nodes: MilestoneFlowNode[],
    now: Date = new Date()
): string | undefined => {
    const date = completedAt(node.milestone);
    if (!date) return undefined;

    if (date.getTime() > now.getTime()) return 'dated in the future';

    const ordered = [...nodes].sort((a, b) => a.position - b.position);
    const index = ordered.findIndex(n => n.id === node.id);
    if (index < 0) return undefined;

    const earlier = [...ordered.slice(0, index)].reverse().find(n => completedAt(n.milestone));
    const earlierDate = completedAt(earlier?.milestone);
    if (earlier && earlierDate && date.getTime() < earlierDate.getTime()) {
        return `dated before ${earlier.name}`;
    }

    const later = ordered.slice(index + 1).find(n => completedAt(n.milestone));
    const laterDate = completedAt(later?.milestone);
    if (later && laterDate && date.getTime() > laterDate.getTime()) {
        return `dated after ${later.name}`;
    }

    return undefined;
};

export const milestoneNodeBehavior = (
    node: MilestoneFlowNode,
    opts: {
        frozen: boolean;
        /** Who recorded this milestone, shown under its date. */
        formatBy?: (node: MilestoneFlowNode) => string | undefined;
        /**
         * The milestone that carries the lead's resolution, and so the one that
         * offers to reopen it. Only a win by funded retainer lives in the
         * pipeline; every other resolution is reopened from its own card.
         */
        reopenPosition?: number | null;
        formatMeta: (node: MilestoneFlowNode) => string | undefined;
        /** Flags a date that disagrees with its neighbours. Never blocks. */
        formatWarning?: (node: MilestoneFlowNode) => string | undefined;
    },
    handlers: MilestoneNodeHandlers
): NodeBehavior => {
    const behavior: NodeBehavior = {
        metaText: opts.formatMeta(node),
        subText: opts.formatBy?.(node),
        // Set before the frozen branch returns: a resolved lead still allows
        // date corrections, so it still needs to be told when one looks wrong.
        warning: opts.formatWarning?.(node),
        durationText: null,
        editableDates: false,
    };

    if (opts.frozen) {
        // A resolved lead stops moving, but a date entered wrongly is still wrong.
        // Correcting one is bookkeeping, not a change to where the lead stands.
        behavior.editableDates = node.stageStatus === 'c';
        behavior.title = behavior.editableDates
            ? 'Click the date to correct it; reopen the lead to change milestones'
            : 'Reopen the lead to change milestones';

        if (opts.reopenPosition === node.position) {
            behavior.actions = [];
            if (handlers.onEditResolution) {
                behavior.actions.push({
                    key: 'edit',
                    label: 'Edit',
                    kind: 'link',
                    title: 'Change the reasons and notes recorded with the win',
                    onClick: handlers.onEditResolution,
                });
            }
            if (handlers.onReopen) {
                behavior.actions.push({
                    key: 'reopen',
                    label: 'Reopen',
                    kind: 'link',
                    title: 'Reopen this lead and undo the win',
                    onClick: handlers.onReopen,
                });
            }
        }
        return behavior;
    }

    if (node.stageStatus === 'c') {
        behavior.clickable = true;
        behavior.onClick = () => handlers.onMoveBack(node);
        behavior.hint = 'Return Here';
        behavior.title = 'Click to undo this milestone and everything after it';
        // Recording a milestone days after it happened is normal, so its date
        // stays editable through the inline editor on the date itself.
        behavior.editableDates = true;
        return behavior;
    }

    if (node.stageStatus === 's') {
        behavior.title = 'Not reached before the lead was resolved';
        return behavior;
    }

    // Not recorded yet. It looks like every other placeholder; only the next one
    // in the order can actually be clicked.
    if (!node.isNext) {
        behavior.title = 'Milestones are recorded in order';
        return behavior;
    }

    const isWin = node.position === WIN_POSITION;
    behavior.clickable = true;
    behavior.onClick = () => isWin ? handlers.onWinPrompt(node) : handlers.onComplete(node);
    // No hover hint: the milestone's own past-tense name already says what
    // clicking it means. The last step is the exception, because that click
    // wins the lead rather than just recording a step.
    if (isWin) behavior.hint = 'Mark as Won';
    behavior.title = isWin
        ? 'Click once the retainer has been funded'
        : `Click once this has happened: ${node.name}`;

    return behavior;
};
